import Button from '@/components/Button'
import GenericInput from '@/components/GenericInput'
import { Select } from '@/components/SelectInputs'
import { useApp } from '@/lib/provider/AppProvider'
import { useMemo, useState } from 'react'
import { WizardSection } from '@/components/SchemaWizard'
import WizardHeader from './partials/WizardHeader'
import { WizardSchema } from '@/lib/definitions/wizard.types'
import WizardContractTerm from './partials/WizardContractTerm'
import { ContractTerm } from './ServiceWizard.types'
import { ProductIcon } from '@/lib/misc/ProductIcons'
import ScrollableCountryLocationsSelector from './partials/ScrollableCountryLocationsSelector'
import { Location } from '@/lib/definitions/api.types'
import {
  DynamicProduct as DynamicProductSchema,
  DynamicProductField,
} from '@/lib/definitions/api.types'
import Toggle from '@/components/Toggle'
import { ApiService } from '@/lib/services/ApiService'
import { useNavigate } from 'react-router-dom'
import { m } from 'framer-motion'
import { Notify } from '@/lib/misc/Notify'
import { cn, validateVLanId } from '@/lib/misc/Utils'
import DynamicProductPortField, {
  DynamicProductPortValue,
  isDynamicProductPortValid,
} from './partials/DynamicProductPortField'

interface DynamicProductProps {
  productId: string
}

const apiService = new ApiService()

const getVlanError = (value: string) =>
  /^\d+$/.test(value)
    ? validateVLanId(Number(value))
    : 'VLAN ID should be a whole number'

const DynamicProduct = ({ productId }: DynamicProductProps) => {
  const navigate = useNavigate()
  const [appState] = useApp()
  const [formData, setFormData] = useState<any>({})
  const [fieldsWithCustomLocations, setFieldsWithCustomLocations] = useState<
    string[]
  >([])
  const [isLoading, setIsLoading] = useState(false)
  const [isSubmitted, setIsSubmitted] = useState(false)

  const handleChange = (field: string, value: any) => {
    setFormData((current: any) => ({ ...current, [field]: value }))
  }

  const appInfo = appState?.appInfo || { products: [] }

  const product: DynamicProductSchema | undefined = useMemo(
    () =>
      appInfo.products.find(
        (product: DynamicProductSchema) => product.product_key === productId
      ),
    [appInfo.products, productId]
  )

  const submit = async () => {
    setIsLoading(true)
    try {
      await apiService.sendDynamicProductRequest({
        dynamic_product: productId,
        payload: formData,
      })
      setIsSubmitted(true)
    } catch (error) {
      console.error('error', error) // eslint-disable-line
      Notify.error('Failed to send request. Please try again later.')
    } finally {
      setIsLoading(false)
    }
  }

  const onToggleCustomLocation = (field: string, checked: boolean) => {
    if (checked) {
      setFieldsWithCustomLocations([...fieldsWithCustomLocations, field])
    } else {
      setFieldsWithCustomLocations(
        fieldsWithCustomLocations.filter((f) => f !== field)
      )
    }
    setFormData({ ...formData, [field]: '' })
  }

  const isFieldValid = (field: string, fieldData: DynamicProductField) => {
    const value = formData[field]
    if (value === undefined || value === '') return false
    if (fieldData.type === 'port') return isDynamicProductPortValid(value)
    if (fieldData.type === 'vlan') return !getVlanError(String(value))
    return true
  }

  const isFormValid = Object.entries(product?.fields || {}).every(
    ([field, fieldData]) => isFieldValid(field, fieldData)
  )

  const sendAnotherRequest = () => {
    setIsSubmitted(false)
    setFormData({})
    setFieldsWithCustomLocations([])
  }

  if (!product) {
    return <div>Product not found</div>
  }

  if (isSubmitted) {
    return (
      <m.div
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5 }}
        className="flex flex-col justify-center items-center min-h-screen max-w-xl mx-auto"
      >
        <div className="flex flex-col items-center gap-4">
          <div className="text-neutral-90">
            <ProductIcon productId={product.product_key} size={60} />
          </div>
          <h1 className="text-3xl font-bold text-neutral-90">
            {product.title} Request Sent
          </h1>
          <p className="text-neutral-50 mt-4 max-w-md text-center">
            Your request has been sent to the {product.title} team. They will
            get back to you as soon as possible.
          </p>
          <div className="flex flex-row gap-2 mt-4">
            <Button style="secondary" onClick={() => navigate('/')}>
              Back to Home
            </Button>
            <Button style="primary" onClick={() => sendAnotherRequest()}>
              Send Another Request
            </Button>
          </div>
        </div>
      </m.div>
    )
  }

  const headerSchema: WizardSchema = {
    id: product.product_key,
    title: product.title,
    description: product.description,
    icon: (
      <ProductIcon
        productId={product.icon || product.product_key}
        size={48}
        className="text-neutral-70"
      />
    ),
    stateSchema: {},
    fields: [],
  }

  const sortedFields = Object.keys(product.fields).sort(
    (a, b) =>
      (product.fields[a]?.position || 0) - (product.fields[b]?.position || 0)
  )

  const renderFieldInput = (field: string, fieldData: DynamicProductField) => {
    if (
      [
        'input',
        'number',
        'email',
        'string',
        'date',
        'datetime',
        'time',
        'password',
        'search',
        'text',
        'url',
        'tel',
      ].includes(fieldData.type)
    ) {
      return (
        <GenericInput
          type={
            ['input', 'string'].includes(fieldData.type)
              ? 'text'
              : fieldData.type
          }
          className="w-full"
          name={field}
          label={fieldData.label}
          value={formData[field] ?? ''}
          disabled={isLoading}
          onChange={(e) => {
            handleChange(field, e.target.value)
          }}
        />
      )
    }

    switch (fieldData.type) {
      case 'select':
        return (
          <Select
            name={field}
            label={fieldData.label}
            options={
              fieldData.options?.map((option) => ({
                value: option,
                label: option,
              })) || []
            }
            value={formData[field] ?? ''}
            disabled={isLoading}
            setValue={(value: string) => {
              handleChange(field, value)
            }}
          />
        )
      case 'location':
        return fieldsWithCustomLocations.includes(field) ? (
          <GenericInput
            type="text"
            name={field}
            label={fieldData.label}
            value={formData[field] ?? ''}
            disabled={isLoading}
            onChange={(e) => {
              handleChange(field, e.target.value)
            }}
          />
        ) : (
          <ScrollableCountryLocationsSelector
            title=""
            selectedLocation={formData[field] || undefined}
            onLocationSelect={(location?: Location) => {
              handleChange(field, location?.id ?? '')
            }}
            includeExtensions={fieldData.include_extensions ?? true}
          />
        )
      case 'port':
        return (
          <DynamicProductPortField
            value={formData[field]}
            includeExtensions={fieldData.include_extensions ?? true}
            onChange={(value: DynamicProductPortValue) => {
              handleChange(field, value)
            }}
          />
        )
      case 'vlan':
        return (
          <GenericInput
            type="number"
            className="w-full"
            name={field}
            label={fieldData.label}
            min={1}
            max={4094}
            placeholder="1-4094"
            value={formData[field] ?? ''}
            disabled={isLoading}
            errors={
              formData[field]
                ? [getVlanError(String(formData[field]))]
                : undefined
            }
            onChange={(e) => {
              handleChange(field, e.target.value)
            }}
          />
        )
      case 'contract_term':
        return (
          <WizardContractTerm
            setDefaultValue={false}
            showTitle={false}
            inline={false}
            contract={formData[field]}
            minContractTerm={12}
            setContractTerm={(value: ContractTerm) => {
              handleChange(field, value.period)
            }}
          />
        )
      default:
        return null
    }
  }

  return (
    <div className="min-h-screen relative">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 pb-40">
        <div className="max-w-5xl mx-auto">
          <WizardHeader schema={headerSchema} className="mb-8 mt-0" />

          <div
            className={cn(
              'space-y-4',
              isLoading && 'opacity-60 transition-opacity duration-300'
            )}
            inert={isLoading || undefined}
          >
            {sortedFields.map((field, index) => {
              const fieldData = product.fields[field]
              return (
                <m.div
                  key={field}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.2, ease: 'easeOut' }}
                >
                  <WizardSection
                    id={`wizard-field-${field}`}
                    title={fieldData.label}
                    description={fieldData.description}
                    stepNumber={index + 1}
                    isComplete={isFieldValid(field, fieldData)}
                    headerAction={
                      fieldData.type === 'location' ? (
                        <div className="text-sm text-neutral-50 flex items-center gap-2">
                          <span>Use custom location</span>
                          <Toggle
                            checked={fieldsWithCustomLocations.includes(field)}
                            onChange={(checked: boolean) => {
                              onToggleCustomLocation(field, checked)
                            }}
                            disabled={isLoading}
                          />
                        </div>
                      ) : undefined
                    }
                  >
                    <div className="p-4 bg-surface rounded-xl shadow-btn border border-neutral-30">
                      {renderFieldInput(field, fieldData)}
                    </div>
                  </WizardSection>
                </m.div>
              )
            })}
          </div>

          <div className="mt-8 w-full overflow-hidden rounded-3xl border border-neutral-20 bg-white flex flex-col md:grid md:grid-cols-3">
            <div className="flex flex-col gap-1 px-7 py-6 md:col-span-2">
              <h3 className="text-heading-byline tracking-tightest text-brand-1">
                Send your request
              </h3>
              <p className="text-sm text-neutral-50">
                The {product.title} team will review your request and get back
                to you as soon as possible.
              </p>
            </div>
            <div className="flex flex-col justify-center gap-2 border-neutral-20 bg-neutral-05 px-7 py-6 md:border-l">
              <Button
                onClick={submit}
                style="primary"
                size="lg"
                className="w-full justify-center"
                disabled={isLoading || !isFormValid}
              >
                {isLoading ? 'Sending Request...' : 'Send Request'}
              </Button>
              {!isFormValid && (
                <p className="text-center text-sm text-neutral-50">
                  Complete all steps to continue.
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default DynamicProduct
